import { i18n } from '@lingui/core';
import { loadCatalog, BUNDLED_LOCALES } from './catalogs';

// Re-export from @lingui/core for convenience
export { i18n };

// Re-export from @lingui/react for convenience
export { Trans, I18nProvider } from '@lingui/react';

// Re-export the useLingui hook
export { useLingui } from '@lingui/react';

/**
 * Supported UI locales (Phase 1).
 */
export const SUPPORTED_LOCALES = ['en', 'fr', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/**
 * All 9 locales from shared types (for OFF product names + future UI expansion).
 */
export const ALL_LOCALES = ['en', 'fr', 'es', 'de', 'it', 'nl', 'pt', 'ja', 'zh'] as const;
export type AnyLocale = (typeof ALL_LOCALES)[number];

/**
 * Check if a locale is supported for UI translation.
 *
 * @param locale - The locale to check
 * @returns True if the locale is supported
 */
export function isSupported(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Normalize a locale string to a supported locale.
 * Extracts the base language from region codes (e.g., 'en-US' -> 'en').
 *
 * @param locale - The locale to normalize
 * @returns A supported locale, falling back to 'en'
 */
export function normalizeLocale(locale: string): SupportedLocale {
  const base = locale.split('-')[0].toLowerCase();
  return isSupported(base) ? base : 'en';
}

/**
 * Activate a locale for i18n.
 * Loads the catalog and activates it in the i18n instance.
 *
 * @param locale - The locale to activate (will be normalized)
 * @returns The normalized locale that was activated
 */
export async function activateLocale(locale: string): Promise<SupportedLocale> {
  const normalized = normalizeLocale(locale);
  const messages = await loadCatalog(normalized);
  i18n.load(normalized, messages);
  i18n.activate(normalized);
  return normalized;
}

/**
 * Check if a locale is bundled (instant load) vs lazy-loaded.
 *
 * @param locale - The locale to check
 * @returns True if the locale is bundled
 */
export function isBundled(locale: SupportedLocale): boolean {
  return BUNDLED_LOCALES.includes(locale);
}

// Re-export from submodules
export { loadCatalog, preloadBundledLocales, BUNDLED_LOCALES, clearCatalogCache } from './catalogs';
export { DEEPL_LOCALE_MAP, getDeepLLocale } from './deepl-locale-map';
export { DATE_FNS_LOCALES, getDateFnsLocale } from './date-fns-locale';
export { translateTourField, translateStepField } from './tour-messages';
export { translatePresetLabel } from './preset-messages';
export { translateGroupName } from './product-group-messages';
